import * as React from 'react';
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import { StylesProps, ThemeType } from 'theme/jss-types';
import { useTranslation } from 'react-i18next';
import {
    Col,
    DatePicker,
    Form,
    FormInstance,
    Input,
    Radio,
    Select,
    TimePicker,
} from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import TimeSlotList from './time-slot-list';
import { commonStyleSheet } from 'library/common-styles';
import { FormField, InputTypes } from 'components/create-consignment/create-consignment.types';
import TextArea from 'antd/lib/input/TextArea';
import { useCallback } from 'react';
import moment from 'moment';
import { formFields } from 'components/create-consignment/create-modal.constants';

const {
    ConsignmentReference,
    ReferenceNumber,
    ForwardReferenceNumber,
    DeliveryDate,
} = formFields;

const styles = (theme: ThemeType) => ({
    formItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'left',
        '& .ant-checkbox + span': {
            color: '#111111',
            fontSize: 14,
            fontWeight: 600,
        },
    },
    title: {
        color: '#333333',
        fontFamily: 'Open Sans',
        fontSize: 12,
        letterSpacing: 0,
        width: '100%',
        marginBottom: 4,
        marginLeft: 4,
    },
    referenceTitle: {
        color: '#333333',
        fontFamily: 'Open Sans',
        fontSize: 12,
        letterSpacing: 0,
        width: '100%',
        marginBottom: 4,
    },
    contentType: {
        ...commonStyleSheet(theme).flexRow,
        '& .ant-form-item': {
            marginBottom: 0,
        },
    },
    formItemWeight: {
        width: '45%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'left',
    },
    dateStyle: {
        width: '100%',
    },
});

interface IProps extends StylesProps<ReturnType<typeof styles>>, ReturnType<typeof mapStateToProps> {
    params: any;
    field: FormField;
    parentObj: string;
    MAX_LIMIT: number;
    colSpan?: number;
    form?: FormInstance;
}

const GenericField = (props: IProps) => {
    const { t, i18n } = useTranslation();
    const {
        classes,
        params,
        field,
        master,
        parentObj,
        MAX_LIMIT,
        colSpan,
        form,
    } = props;
    const {
        isRequired,
        pieceIndex,
        key,
        itemIndex,
        enableCustomFilter = false,
        getValidationRules,
        getIsRequiredValidation,
        disabledFields = {},
        editData,
    } = params;

    const isItemLevel = itemIndex !== null && itemIndex !== undefined;
    const isPieceLevel = pieceIndex !== null && pieceIndex !== undefined;
    let formFieldKey = null;
    if (isItemLevel) {
        formFieldKey = [parentObj, itemIndex, field.key];
    } else if (parentObj) {
        formFieldKey = [parentObj, field.key];
    } else {
        formFieldKey = field.key;
    }

    const isFieldDisabled = useCallback(() => {
        if (editData) {
            if (
                key?.includes(ReferenceNumber.key)
                || key?.includes(ConsignmentReference.key)
                || key?.includes(ForwardReferenceNumber.key)) {
                return true;
            }
        }
        if (field.disabled) {
            return true;
        }
        if (!disabledFields) return false;
        if (parentObj && disabledFields[parentObj]) return true;
        if (isPieceLevel && disabledFields.piece) return true;
        if (isPieceLevel && disabledFields[`piece_${field.key}`]) return true;
        if (key && disabledFields[key]) return true;
        if (Array.isArray(formFieldKey)) {
            if (disabledFields[formFieldKey.join('.')]) return true;
        } else if (disabledFields[formFieldKey]) {
            return true;
        }
        return false;
    }, [editData, key, disabledFields, formFieldKey, field.disabled]);

    const handleDisabledPickupDate = (currentDate: any) => {
        if (!params.shouldNotSelectPastDate) {
            return false;
        }
        return currentDate && currentDate.format('YYYY-MM-DD') < moment().format('YYYY-MM-DD');
    };

    const renderTitle = (fieldData: FormField) => {
        return (
            <div
                className={classes.title}
            >
                {t(fieldData.key) || (fieldData.titleKey ? t(fieldData.titleKey) : fieldData.label) || ''}
                {isRequired(isPieceLevel, fieldData.key)
                    || getIsRequiredValidation(fieldData.key, isPieceLevel) ? '*' : null}
            </div>
        );
    };

    const renderInput = (fieldData: FormField) => {
        const rulesList = getValidationRules(fieldData, isPieceLevel, { MAX_LIMIT });
        return (
            <Form.Item
                name={key || formFieldKey}
                initialValue={fieldData.defaultValue}
                valuePropName={fieldData.valuePropName}
                rules={rulesList}
            >
                <Input
                    type={fieldData.inputType || 'text'}
                    min={0}
                    placeholder={t(fieldData.key)}
                    style={{ width: '100%' }}
                    disabled={isFieldDisabled()}
                />
            </Form.Item>
        );
    };

    const renderTextarea = (fieldData: any) => {
        const rulesList = getValidationRules(fieldData, isPieceLevel, { MAX_LIMIT });
        return (
            <Form.Item
                name={key || formFieldKey}
                initialValue={fieldData.defaultValue}
                valuePropName={fieldData.valuePropName}
                rules={rulesList}
            >
                <TextArea
                    placeholder={t(fieldData.placeholder)}
                    disabled={isFieldDisabled()}
                />
            </Form.Item>
        );
    };

    const renderRadio = (fieldData: FormField) => {
        const rulesList = getValidationRules(fieldData, isPieceLevel, { MAX_LIMIT });
        return (
            <Form.Item
                name={key || formFieldKey}
                valuePropName={fieldData.valuePropName}
                initialValue={fieldData.defaultValue}
                rules={rulesList}
            >
                <Radio.Group
                    options={fieldData.options}
                    defaultValue={fieldData.defaultValue}
                    disabled={isFieldDisabled()}
                />
            </Form.Item>
        );
    };

    const renderSelect = (fieldData: FormField) => {
        const rulesList = getValidationRules(fieldData, isPieceLevel, { MAX_LIMIT });
        const options = fieldData.getOptions
            ? (fieldData.getOptions(master) || fieldData.options)
            : fieldData.options;
        if (Array.isArray(options) && options.length > 0) {
            options.forEach((item: any) => {
                /* eslint-disable no-param-reassign */
                item.label = i18n.exists(item.value) ? t(item.value) : item.label;
            });
        }
        return (
            <Form.Item
                name={key || formFieldKey}
                initialValue={fieldData.defaultValue}
                valuePropName={fieldData.valuePropName}
                rules={rulesList}
            >
                <Select
                    style={{ width: '100%' }}
                    showSearch
                    disabled={isFieldDisabled()}
                    placeholder={t(fieldData.key)}
                    options={fieldData.options}
                    allowClear
                    filterOption={
                        enableCustomFilter
                            ? (input: any, option: any) => {
                                return (
                                    option?.value?.toString().toLowerCase().includes(input.toLowerCase())
                                    || option?.label?.toString().toLowerCase().includes(input.toLowerCase())
                                );
                            }
                            : undefined
                    }
                />
            </Form.Item>
        );
    };

    const renderDate = (fieldData: FormField) => {
        const rulesList = getValidationRules(fieldData, isPieceLevel, { MAX_LIMIT });
        return (
            <Form.Item
                name={key || formFieldKey}
                initialValue={fieldData.defaultValue}
                valuePropName={fieldData.valuePropName}
                rules={rulesList}
            >
                <DatePicker
                    className={classes.dateStyle}
                    placeholder={fieldData.placeholder}
                    disabledDate={handleDisabledPickupDate}
                    disabled={isFieldDisabled()}
                />
            </Form.Item>
        );
    };

    const renderTimeSlot = (fieldData: FormField) => {
        const rulesList = getValidationRules(fieldData, isPieceLevel, { MAX_LIMIT });
        return (
            <Form.Item
                name={key || formFieldKey}
                initialValue={fieldData.defaultValue}
                valuePropName={fieldData.valuePropName}
                rules={rulesList}
            >
                <TimePicker.RangePicker
                    className={classes.dateStyle}
                    disabled={isFieldDisabled()}
                />
            </Form.Item>
        );
    };

    const renderTimeSlotList = (fieldData: FormField) => {
        if (!form) return null;
        const rulesList = getValidationRules(fieldData, isPieceLevel, { MAX_LIMIT });
        return (
            <Form.Item
                name={key || formFieldKey}
                rules={rulesList}
                valuePropName="value"
            >
                <TimeSlotList
                    form={form}
                    formKey={key || formFieldKey}
                    maxSlots={10}
                />
            </Form.Item>
        );
    };

    const renderFormItem = (
        fieldData: FormField,
    ) => {
        switch (fieldData.type) {
            case InputTypes.Input: return renderInput(fieldData);
            case InputTypes.Number: return renderInput({
                ...fieldData,
                inputType: 'number',
            });
            case InputTypes.Select: return renderSelect(fieldData);
            case InputTypes.Textarea: return renderTextarea(fieldData);
            case InputTypes.Radio: return renderRadio(fieldData);
            case InputTypes.Date: return renderDate(fieldData);
            case InputTypes.TimeSlot: return renderTimeSlot(fieldData);
            case InputTypes.TimeSlotList: return renderTimeSlotList(fieldData);
            case InputTypes.Hidden: return null;
            case InputTypes.Custom: return null;
            default: return 'Input is not defined';
        }
    };

    const { destinationHubTimezone, useHubTimezoneForStorage } = params || {};
    const showTimezoneBanner = field.key === DeliveryDate.key && destinationHubTimezone && useHubTimezoneForStorage;

    return (
        <>
            {showTimezoneBanner && (
                <Col span={24}>
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '4px 12px',
                            marginBottom: 12,
                            border: '1px solid #91caff',
                            borderRadius: 20,
                            background: '#e6f4ff',
                            color: '#0958d9',
                            fontSize: 12,
                            fontFamily: 'Open Sans',
                        }}
                    >
                        <ClockCircleOutlined style={{ fontSize: 12 }} />
                        {`All times in ${destinationHubTimezone}`}
                    </span>
                </Col>
            )}
            <Col span={colSpan || 24} xs={colSpan || 24} sm={colSpan || 24} md={colSpan || 12}>
                <div className={classes.formItem}>
                    {renderTitle(field)}
                    {renderFormItem(field)}
                </div>
            </Col>
        </>
    );
};

const mapStateToProps = (state: any) => {
    const { master } = state;
    return {
        master,
    };
};

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectJss: {
        useJss: true,
        styleSheet: styles,
    },
};

export default GenericHoc(hocConfig)(GenericField);
